import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatError, FALLBACK_ARTIST } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { FileUpload } from "./FileUpload";
import { LinksFields, Field, inputCls } from "./LinksFields";
import { ConfirmDelete } from "./ConfirmDelete";

const EMPTY = { name: "", genre: "", city: "", bio: "", photo_url: "", links: {} };

const ArtistForm = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState({ ...EMPTY, ...initial });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    try {
      f.id ? await api.put(`/admin/artists/${f.id}`, f) : await api.post("/admin/artists", f);
      toast.success("Artista salvato");
      onSaved();
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <form onSubmit={save} className="space-y-6" data-testid="artist-form">
      <Field label="Foto"><FileUpload value={f.photo_url} onChange={(u) => set("photo_url", u)} testid="artist-photo-upload" /></Field>
      <div className="grid sm:grid-cols-3 gap-4">
        <Field label="Nome *"><Input required value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="artist-name-input" /></Field>
        <Field label="Genere"><Input value={f.genre} onChange={(e) => set("genre", e.target.value)} className={inputCls} data-testid="artist-genre-input" /></Field>
        <Field label="Città"><Input value={f.city} onChange={(e) => set("city", e.target.value)} className={inputCls} data-testid="artist-city-input" /></Field>
      </div>
      <Field label="Biografia"><Textarea rows={5} value={f.bio} onChange={(e) => set("bio", e.target.value)} className={inputCls} data-testid="artist-bio-input" /></Field>
      <LinksFields links={f.links} onChange={(l) => set("links", l)} keys={["instagram", "facebook", "spotify", "soundcloud", "beatport", "youtube"]} prefix="artist" />
      <div className="flex gap-3">
        <button className="rounded-full bg-emerald-500 text-black px-8 py-3 font-semibold hover:bg-emerald-400" data-testid="artist-save-btn">Salva artista</button>
        <button type="button" onClick={onCancel} className="rounded-full border border-white/15 px-6 py-3 text-sm" data-testid="artist-cancel-btn">Annulla</button>
      </div>
    </form>
  );
};

export const AdminArtists = ({ onChange }) => {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/artists").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    try { await api.delete(`/admin/artists/${id}`); toast.success("Artista eliminato"); load(); onChange?.(); }
    catch (e) { toast.error(formatError(e)); }
  };

  if (editing) return (
    <div>
      <h2 className="font-display text-2xl font-bold uppercase tracking-tight mb-8">{editing.id ? "Modifica artista" : "Nuovo artista"}</h2>
      <ArtistForm initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onChange?.(); }} />
    </div>
  );

  return (
    <div data-testid="admin-artists">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl font-bold uppercase tracking-tight">Artisti</h2>
        <button onClick={() => setEditing({})} data-testid="admin-new-artist-btn" className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-black px-5 py-2.5 text-sm font-semibold hover:bg-emerald-400"><Plus className="h-4 w-4" /> Nuovo artista</button>
      </div>
      {!list.length && <p className="text-sm text-zinc-400 border border-dashed border-white/15 p-10 text-center">Nessun artista. Aggiungi te stesso come primo artista!</p>}
      <div className="grid sm:grid-cols-2 gap-3">
        {list.map((a) => (
          <div key={a.id} className="flex items-center gap-4 border border-white/10 bg-[#0d0e12] p-3" data-testid={`admin-artist-row-${a.id}`}>
            <img src={fileUrl(a.photo_url) || FALLBACK_ARTIST} alt="" className="h-14 w-14 object-cover rounded-full" />
            <div className="flex-1 min-w-0"><p className="font-semibold truncate">{a.name}</p><p className="text-xs text-zinc-400 truncate">{a.genre}</p></div>
            <button onClick={() => setEditing(a)} className="p-2 text-zinc-400 hover:text-white" aria-label="Modifica" data-testid={`admin-edit-artist-${a.id}`}><Pencil className="h-4 w-4" /></button>
            <ConfirmDelete onConfirm={() => remove(a.id)} testid={`admin-delete-artist-${a.id}`} title={`Eliminare ${a.name}?`}><Trash2 className="h-4 w-4" /></ConfirmDelete>
          </div>
        ))}
      </div>
    </div>
  );
};
